import '../scss/_index.scss'
import { getNearestSite, useLogger } from '@omnia/ui/src/lib'
import { getBemClassBuilder } from '@omnia/ui/src/lib/lumos-plus/utils/get-bem-class-builder'
import type { DropdownOption } from '@vista-digital/occ-ui'
import { Button, ButtonColor, DropdownField, ModalMode } from '@vista-digital/occ-ui'
import { Modal } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/modal'
import type { IndexKey } from '@vista-digital/utilities'
import { observer } from 'mobx-react'
import { type FC, useEffect, useMemo, useState } from 'react'
import { cmsDataService } from '@/common/services/cms.data.service'
import { getCinemaGroups } from '../utils/get-cinema-groups'
import { getSelectedCinemaGroup } from '../utils/get-selected-cinema-group'
import { setSelectedCinemaGroup } from '../utils/set-selected-cinema-group'

const ComponentName = 'RequiredCinemaGroupPrompt'
const b = getBemClassBuilder(ComponentName)
const lastNearestCinemaGroupId = 'lastNearestCinemaGroupId'

const getLastNearestCinemaGroupId = (): IndexKey | undefined => {
    if (typeof window === 'undefined') return undefined

    return window.localStorage.getItem(lastNearestCinemaGroupId) ?? undefined
}

const setLastNearestCinemaGroupId = (cinemaGroupId: IndexKey) => {
    if (typeof window === 'undefined') return

    window.localStorage.setItem(lastNearestCinemaGroupId, String(cinemaGroupId))
}

export const RequiredCinemaGroupPrompt: FC = observer(() => {
    const logger = useLogger(ComponentName)

    const selectedGroup = getSelectedCinemaGroup()
    const cinemaGroups = getCinemaGroups()

    const [selectedDropdownOption, setSelectedDropdownOption] = useState<IndexKey | undefined>(undefined)
    const [shouldShowPrompt, setShouldShowPrompt] = useState(false)

    const dropdownOptions: DropdownOption[] = useMemo(
        () =>
            cinemaGroups.map(cinemaGroup => ({
                value: cinemaGroup.id,
                text: cinemaGroup.name,
            })) ?? [],
        [cinemaGroups]
    )

    const {
        heading = 'Select Cinema Group',
        dropdownLabel = 'City',
        dropdownPlaceholder = 'Select',
        submitButtonLabel = 'Submit',
    } = cmsDataService.get().requiredCinemaGroupPrompt ?? {}

    // NOTE:
    // We re-check user location on every page load / re-render.
    // Browsers do NOT emit location-change events, so this logic
    // ensures we detect location changes when user revisits or reloads the site.
    useEffect(() => {
        if (cinemaGroups.length === 0) return

        let isCancelled = false

        void (async () => {
            const nearestSite = await getNearestSite()
            if (isCancelled || !nearestSite) return

            const nearestGroup = cinemaGroups.find(group =>
                group.cinemas?.some(cinema => String(cinema.vistaCinema?.key) === String(nearestSite.id))
            )
            const nearestGroupId = nearestGroup?.id
            const lastNearestGroupId = getLastNearestCinemaGroupId()

            const hasPhysicalLocationChanged =
                lastNearestGroupId !== undefined && String(lastNearestGroupId) !== String(nearestGroupId)

            if (!nearestGroupId) return

            setLastNearestCinemaGroupId(nearestGroupId)

            if (!selectedGroup) {
                setSelectedDropdownOption(nearestGroupId)
                return
            }

            if (!hasPhysicalLocationChanged) {
                setShouldShowPrompt(false)
                return
            }

            setSelectedDropdownOption(nearestGroupId)
            setShouldShowPrompt(true)

            logger.info('Cinema group prompt triggered', {
                selectedGroup,
                nearestGroup,
                nearestSite,
                lastNearestGroupId,
            })
        })()

        return () => {
            isCancelled = true
        }
    }, [cinemaGroups, logger, selectedDropdownOption, selectedGroup])

    if (dropdownOptions.length === 0) {
        logger.warn('Cinema groups are required, but none have been found.')
        return null
    }

    return (
        <>
            <Modal
                className={b.block()}
                mode={ModalMode.Overlay}
                canBeClosed={false}
                show={!selectedGroup || shouldShowPrompt}
                heading={heading}
            >
                <DropdownField
                    id={'cinema-group-dropdown'}
                    value={selectedDropdownOption}
                    onChangeValue={setSelectedDropdownOption}
                    error={undefined}
                    options={dropdownOptions}
                    displayText={{
                        label: dropdownLabel,
                        placeholder: dropdownPlaceholder,
                    }}
                />

                <Button
                    className={b.element('submit-button')}
                    color={ButtonColor.Primary}
                    label={submitButtonLabel}
                    disabled={!selectedDropdownOption}
                    onClick={() => {
                        if (selectedDropdownOption) {
                            const selected = cinemaGroups.find(group => group.id === selectedDropdownOption) || null
                            setSelectedCinemaGroup(selected)
                            setShouldShowPrompt(false)
                        }
                    }}
                />
            </Modal>
        </>
    )
})

RequiredCinemaGroupPrompt.displayName = ComponentName
